import React, { useState } from 'react';
import { X, Search, Loader, Package, Tag, Image, CheckCircle, AlertCircle } from 'lucide-react';

const ProductScrapeModal = ({ isOpen, onClose, category, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Input, 2: Preview
  const [modelName, setModelName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);

  const requestValues = () => {
    const fullName = modelName.trim();
    const brandName = fullName.split(/\s+/)[0];
    const normalizedModel = fullName.replace(new RegExp(`^${brandName}\\s+`, 'i'), '').trim();
    return { brandName, normalizedModel };
  };

  const parsePayload = (value) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      throw new Error('Server returned an invalid response');
    }
  };

  const handlePreview = async () => {
    if (!modelName.trim()) {
      setError('Please enter model name');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Extract brand name from model (first word usually)
      const { brandName, normalizedModel } = requestValues();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            brand_name: brandName,
            model_name: normalizedModel,
            category: category
          })
        }
      );

      const data = parsePayload(await response.json());

      if (response.ok) {
        if (data.product_exists) {
          setError('This product already exists in the system');
        } else {
          setPreviewData(data);
          setStep(2);
        }
      } else {
        setError(data.detail || 'Failed to fetch product preview');
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError('Failed to fetch product preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    try {
      setLoading(true);
      setError('');

      // Extract brand name from model
      const { brandName, normalizedModel } = requestValues();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/scrape`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            brand_name: brandName,
            model_name: normalizedModel,
            category: category
          })
        }
      );

      const data = parsePayload(await response.json());

      if (response.ok) {
        alert(`✓ Product added successfully!\nSKU: ${data.product.sku_code}`);
        onSuccess();
        handleClose();
      } else {
        setError(data.detail || 'Failed to add product');
      }
    } catch (err) {
      console.error('Error adding product:', err);
      setError('Failed to add product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setModelName('');
    setError('');
    setPreviewData(null);
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setPreviewData(null);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-primary-foreground flex items-center gap-2">
              <Package className="w-5 h-5" />
              {step === 1 ? 'Add Product' : 'Product Preview'}
            </h3>
            <button
              onClick={handleClose}
              className="text-primary-foreground hover:text-primary-foreground/80"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            {step === 1 ? (
              /* Step 1: Input Form */
              <div className="space-y-4">
                {/* Category Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>

                {/* Model Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="e.g., Samsung Galaxy S23 Ultra"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    autoFocus
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter full model name (e.g., Samsung Galaxy S23 Ultra, Apple iPhone 15 Pro)
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Brand will be auto-detected and created if needed
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Info */}
                <div className="bg-info/10 border border-info/20 p-3 rounded">
                  <p className="text-sm text-info">
                    <strong>How it works:</strong> System will automatically detect the brand, scrape complete product details from GSMArena, and create all ZUPC variants. Pricing will be entered in the next step.
                  </p>
                </div>
              </div>
            ) : (
              /* Step 2: Preview */
              <div className="space-y-6">
                {/* Brand Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Tag className="w-5 h-5" />
                    Brand Information
                  </h4>
                  <div className="flex items-center gap-3">
                    {previewData?.brand?.logo_url && (
                      <img 
                        src={previewData.brand.logo_url} 
                        alt={previewData.brand.name}
                        className="w-12 h-12 object-contain"
                      />
                    )}
                    <div>
                      <p className="font-medium">{previewData?.brand?.name}</p>
                      {previewData?.brand?.action === 'will_create' && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          New Brand - Will be created
                        </span>
                      )}
                      {previewData?.brand?.action === 'exists' && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Existing Brand
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Product Details
                  </h4>
                  <div className="space-y-2">
                    <p><strong>SKU:</strong> {previewData?.product?.sku_code}</p>
                    <p><strong>Model:</strong> {previewData?.product?.model_name}</p>
                    <p><strong>Category:</strong> {previewData?.product?.category}</p>
                    {previewData?.product?.release_date && (
                      <p><strong>Release:</strong> {previewData.product.release_date}</p>
                    )}
                  </div>
                </div>

                {/* Images Preview */}
                {previewData?.product?.images?.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Product Images ({previewData.product.images.length})
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {previewData.product.images.slice(0, 4).map((img, idx) => (
                        <img 
                          key={idx}
                          src={img} 
                          alt={`Product ${idx + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing Info */}
                <div className="bg-warning/10 border border-warning/20 p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">💰 Pricing Entry</h4>
                  <p className="text-sm text-muted-foreground">
                    After creating this product, you'll be prompted to enter MRP/FSP for each RAM+ROM combination.
                  </p>
                </div>

                {/* Scraped Data Display */}
                {previewData?.variants?.total_combinations > 0 && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">📋 Scraped Data from GSMArena</h4>
                    
                    {/* Memory Variants */}
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">💾 Memory Options ({previewData.variants.memory_options.length}):</p>
                      <div className="flex flex-wrap gap-2">
                        {previewData.variants.memory_options.map((mem, idx) => (
                          <span key={idx} className="inline-block px-3 py-1 bg-white border border-blue-300 rounded text-xs font-medium">
                            {mem.display}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Color Variants */}
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">🎨 Color Options ({previewData.variants.color_options.length}):</p>
                      <div className="flex flex-wrap gap-2">
                        {previewData.variants.color_options.map((color, idx) => (
                          <span key={idx} className="inline-block px-3 py-1 bg-white border border-blue-300 rounded text-xs">
                            {color}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Total Calculation */}
                    <div className="bg-purple-100 border border-purple-300 p-3 rounded mt-3">
                      <p className="text-sm font-semibold text-purple-900">
                        🔢 Total ZUPCs: {previewData.variants.memory_options.length} × {previewData.variants.color_options.length} = {previewData.variants.total_combinations} variants
                      </p>
                    </div>

                    {/* Sample ZUPCs */}
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Sample ZUPCs to be created:</p>
                      <div className="text-xs text-gray-600 max-h-40 overflow-y-auto bg-white p-2 rounded border">
                        {previewData.variants.zupcs_to_create.slice(0, 10).map((zupc, idx) => (
                          <div key={idx} className="py-1 border-b border-gray-100 last:border-0">
                            <span className="font-mono text-xs">{idx + 1}. {zupc.zupc}</span>
                            <span className="text-gray-500 ml-2">({zupc.display})</span>
                          </div>
                        ))}
                        {previewData.variants.total_combinations > 10 && (
                          <div className="text-gray-500 italic pt-2">
                            ...and {previewData.variants.total_combinations - 10} more variants
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="font-semibold mb-1">Ready to Add</p>
                      <p>{previewData?.stats?.specifications_count || 0} specifications • {previewData?.stats?.images_count || 0} images</p>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            {step === 1 ? (
              <>
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Loading Preview...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Preview Product
                    </>
                  )}
                </button>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2b3380] sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleProceed}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-success text-success-foreground hover:bg-success/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Adding Product...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Proceed & Add Product
                    </>
                  )}
                </button>
                <button
                  onClick={handleBack}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2b3380] sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Back
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductScrapeModal;
